import { useQuery } from "@tanstack/react-query";
import { levelAtLeast, PERMISSION_MODULES, type MeResponse, type ModuleKey, type ModuleLevel } from "@klantpunt/shared";
import { api, ApiError } from "./api";

export const meKey = ["me"] as const;

/** Huidige gebruiker; `null` als niet ingelogd. */
export function useMe() {
  return useQuery({
    queryKey: meKey,
    queryFn: async () => {
      try {
        return await api<MeResponse>("/me");
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
  });
}

/**
 * Mag dit (permissie van de rol én het niveau in de bijbehorende module, zoals de server het
 * afdwingt)? Zie PERMISSION_MODULES in @klantpunt/shared.
 */
export const can = (me: MeResponse | null | undefined, permission: string) => {
  if (!me?.permissions.includes(permission)) return false;
  const mod = PERMISSION_MODULES[permission];
  return !mod || levelAtLeast(me.modules[mod[0]] ?? "none", mod[1]);
};

/** Niveau in een module (organisatie × gebruiker). */
export const moduleLevel = (me: MeResponse | null | undefined, module: ModuleKey): ModuleLevel =>
  me?.modules[module] ?? "none";
/** Heeft de gebruiker minstens dit niveau in de module? */
export const hasModule = (me: MeResponse | null | undefined, module: ModuleKey, level: ModuleLevel = "view") =>
  levelAtLeast(moduleLevel(me, module), level);

/** Module-toegang in een component (menu, tabbladen, knoppen volgen de rechten van de server). */
export function useModuleAccess(module: ModuleKey) {
  const { data: me } = useMe();
  const level = moduleLevel(me, module);
  return { level, canView: levelAtLeast(level, "view"), canEdit: levelAtLeast(level, "edit") };
}
